/**
 * Resolver registry — manages custom condition/compute resolvers.
 * Resolvers are registered once and referenced by ID in schemas.
 */

import type { CustomResolver, ResolverContext } from '../types/renderer';
import type { ConditionContext } from '../evaluators/conditionEvaluator';

const conditionResolvers = new Map<string, (ctx: ConditionContext) => boolean>();
const computeResolvers = new Map<string, (ctx: ConditionContext) => unknown>();
const validationRegistry = new Map<string, (value: unknown, formData: Record<string, unknown>, context: ConditionContext) => string | null>();

export const ResolverRegistry = {
  registerCondition(id: string, resolver: (ctx: ConditionContext) => boolean): void {
    conditionResolvers.set(id, resolver);
  },

  registerCompute(id: string, resolver: (ctx: ConditionContext) => unknown): void {
    computeResolvers.set(id, resolver);
  },

  registerMapper(id: string, _mapper: (data: unknown) => Record<string, unknown>): void {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[form-engine] registerMapper("${id}") is removed. ` +
          `Return form-shaped data from the fetch data action instead of mapId transformers.`,
      );
    }
  },

  registerValidation(id: string, validator: (value: unknown, formData: Record<string, unknown>, context: ConditionContext) => string | null): void {
    validationRegistry.set(id, validator);
  },

  getConditionResolver(id: string): ((ctx: ConditionContext) => boolean) | undefined {
    return conditionResolvers.get(id);
  },

  getComputeResolver(id: string): ((ctx: ConditionContext) => unknown) | undefined {
    return computeResolvers.get(id);
  },

  getMapper(_id: string): ((data: unknown) => Record<string, unknown>) | undefined {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[form-engine] getMapper() is removed. Data actions must return form-shaped payloads.',
      );
    }
    return undefined;
  },

  getValidation(id: string): ((value: unknown, formData: Record<string, unknown>, context: ConditionContext) => string | null) | undefined {
    return validationRegistry.get(id);
  },

  getCustomResolvers(): Record<string, (ctx: ConditionContext) => unknown> {
    const all: Record<string, (ctx: ConditionContext) => unknown> = {};
    for (const [id, fn] of conditionResolvers) {
      all[id] = fn as (ctx: ConditionContext) => unknown;
    }
    for (const [id, fn] of computeResolvers) {
      all[id] = fn;
    }
    return all;
  },

  clear(): void {
    conditionResolvers.clear();
    computeResolvers.clear();
    validationRegistry.clear();
  },
};
